import {
  API_URL,
  CustomFilterCityItem,
  CustomFilterItem,
  DailyObsResponse,
  EditableSitesResponse,
  endpoints,
  ForecastSourceResponse,
  ReleaseNotesApiResponse,
  SubRegionToSitesMappingDataResponse,
  UserSegmentDataResponse,
  FORECAST_RANGE_VARIABLES,
} from '@ag2-weathermaker/shared/types/api.constants';
import { DataRegion } from '@ag2-weathermaker/shared/types/enums';
import { UserAllowedResponse } from '@ag2-weathermaker/shared/types/main-content.types';
import {
  CreateGraphicsApiPayload,
  ForecastMetaDataApiResponse,
  SaveAsSourceApiPayload,
  SavedAsSourceApiResponse,
  SavedSourcesApiResponse,
} from '@ag2-weathermaker/shared/types/publishing-center.types';
import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { FeatureCollection } from 'geojson';
import { Observable } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class ApiService {
  private http = inject(HttpClient);

  getEditableSites(dataRegion: DataRegion): Observable<EditableSitesResponse> {
    return this.http.get<EditableSitesResponse>(`${API_URL}${endpoints.editableSites}/${dataRegion}`);
  }

  getNormalsSignedUrl(region: DataRegion): Observable<string> {
    return this.http.get(`${API_URL}${endpoints.staticNormals}/${region}`, {
      responseType: 'text',
    });
  }

  getSiteMetaData(defaultRegion: DataRegion): Observable<FeatureCollection> {
    return this.http.get<FeatureCollection>(`${API_URL}${endpoints.staticSites}/${defaultRegion}`);
  }

  fetchParquetFile(signedUrl: string): Observable<ArrayBuffer> {
    return this.http.get(signedUrl, {
      responseType: 'arraybuffer', // Fetch the file as binary data
    });
  }

  fetchFlatGeoBuff(signedUrl: string): Observable<ArrayBuffer> {
    return this.http.get(signedUrl, {
      responseType: 'arraybuffer',
    });
  }

  getForecastSources(defaultRegion: DataRegion): Observable<ForecastSourceResponse[]> {
    return this.http.get<ForecastSourceResponse[]>(
      `${API_URL}${endpoints.forecastSources}/${defaultRegion}/${FORECAST_RANGE_VARIABLES.shortRange}`
    );
  }

  // the url is a partial url to the parquet file of selected source
  getForecastData(url: string): Observable<string> {
    return this.http.get(`${API_URL}${url}`, {
      responseType: 'text',
    });
  }

  getSavedSources(region = DataRegion.NA): Observable<SavedSourcesApiResponse[]> {
    return this.http.get<SavedSourcesApiResponse[]>(`${API_URL}${endpoints.savedSources}/${region}`);
  }

  getSavedSourcedata(id: string): Observable<string> {
    return this.http.get(`${API_URL}${endpoints.savedSourceData}/${id}`, {
      responseType: 'text',
    });
  }
  saveAsSource(payload: SaveAsSourceApiPayload): Observable<SavedAsSourceApiResponse> {
    return this.http.post<SavedAsSourceApiResponse>(`${API_URL}${endpoints.saveAsSource}`, payload);
  }

  createGraphics(payload: CreateGraphicsApiPayload): Observable<SavedAsSourceApiResponse> {
    return this.http.post<SavedAsSourceApiResponse>(`${API_URL}${endpoints.createGraphics}`, payload);
  }

  isUserAllowed(): Observable<UserAllowedResponse> {
    return this.http.get<UserAllowedResponse>(`${API_URL}${endpoints.isUserAllowed}`);
  }

  getPublishHistory(region: DataRegion): Observable<ForecastMetaDataApiResponse[]> {
    return this.http.get<ForecastMetaDataApiResponse[]>(`${API_URL}${endpoints.publishHistory}/${region}`);
  }

  getUserSegmentData(): Observable<UserSegmentDataResponse> {
    return this.http.get<UserSegmentDataResponse>(`${API_URL}${endpoints.userSegmentData}`);
  }

  getSubRegionToSitesMappingData(region: DataRegion): Observable<SubRegionToSitesMappingDataResponse[]> {
    return this.http.get<SubRegionToSitesMappingDataResponse[]>(
      `${API_URL}${endpoints.subRegionToSitesMappingData}/${region}`
    );
  }

  releaseNotesUpdateSeenStatus(): Observable<void> {
    return this.http.put<void>(`${API_URL}${endpoints.releaseNotesUpdateSeenStatus}`, null);
  }

  releaseNotesHasViewed(): Observable<{ has_viewed: boolean }> {
    return this.http.get<{ has_viewed: boolean }>(`${API_URL}${endpoints.releaseNotesHasViewed}`);
  }

  releaseNotesYears(): Observable<{ years: string[] }> {
    return this.http.get<{ years: string[] }>(`${API_URL}${endpoints.releaseNotesYears}`);
  }

  releaseNotesByYear(year: string): Observable<ReleaseNotesApiResponse[]> {
    return this.http.get<ReleaseNotesApiResponse[]>(`${API_URL}${endpoints.releaseNotes}/${year}`);
  }

  getDailyObs(region: DataRegion, startDate: string, endDate: string): Observable<DailyObsResponse[]> {
    return this.http.get<DailyObsResponse[]>(`${API_URL}${endpoints.dailyObs}/${region}`, {
      params: { start_date: startDate, end_date: endDate },
    });
  }

  public getDailyEC46Data(region: DataRegion, startDate: string): Observable<string> {
    return this.http.get(`${API_URL}daily-ec46/${region}`, {
      params: { start_date: startDate },
      responseType: 'text',
    });
  }

  getCustomFilters(region: DataRegion): Observable<CustomFilterItem[]> {
    return this.http.get<CustomFilterItem[]>(`${API_URL}${endpoints.customFilters}/`, {
      params: { region },
    });
  }

  getCustomFilterCities(profileListId: number, region: DataRegion): Observable<CustomFilterCityItem[]> {
    return this.http.get<CustomFilterCityItem[]>(`${API_URL}${endpoints.customFilterCities}/${profileListId}`, {
      params: { region },
    });
  }
}
